import { useEffect, useMemo, useState } from "react"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import SearchSelect from "Components/molecule/inputs/search-select"
import MultiSelect from "Components/molecule/inputs/multi-select"
import TextInput from "Components/molecule/inputs/text-input"
import { SelectOption } from "Components/molecule/inputs/select-input"
import { OpportunityDashboardFilters, OpportunityDashboardSearchFields } from "models/opportunity-dashboard"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"

interface SearchOpportunityDashboardFormProps {
    fiscalYears: SelectOption[]
    searchFields: OpportunityDashboardSearchFields | null
    filters: OpportunityDashboardFilters
    onChange: (filters: OpportunityDashboardFilters) => void
    onClear: () => void
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const pick = (options: SelectOption[], values: (string | number)[]) =>
    options.filter(o => values.map(String).includes(o.value))

// Master filters shared by every tab of the Opportunities dashboard.
const SearchOpportunityDashboardForm = ({ fiscalYears, searchFields, filters, onChange, onClear }: SearchOpportunityDashboardFormProps) => {
    // Description search applies on Enter or when leaving the field, not on every keystroke.
    const [search, setSearch] = useState(filters.search)
    useEffect(() => setSearch(filters.search), [filters.search])
    const applySearch = () => {
        if (search.trim() !== filters.search) onChange({ ...filters, search: search.trim() })
    }

    const divisionOptions = useMemo<SelectOption[]>(
        () => (searchFields?.divisions ?? []).map(d => ({ value: d.id, label: d.description })),
        [searchFields])

    // Picking divisions narrows the department list to the departments under them.
    const departmentOptions = useMemo<SelectOption[]>(
        () => (searchFields?.departments ?? [])
            .filter(d => filters.divisionIds.length === 0 || (d.parentId !== null && filters.divisionIds.includes(d.parentId)))
            .map(d => ({ value: d.id, label: d.description })),
        [searchFields, filters.divisionIds])

    const branchOptions = useMemo<SelectOption[]>(
        () => (searchFields?.branches ?? []).map(b => ({ value: b.id, label: b.name })),
        [searchFields])

    const monthOptions = useMemo<SelectOption[]>(
        () => Array.from({ length: 12 }, (_, i) => ({
            value: String(i + 1),
            label: calendar.fiscalToLabel(filters.fiscalYear, i + 1),
        })),
        [filters.fiscalYear])

    const onChangeDivisions = (values: SelectOption[]) => {
        const divisionIds = values.map(v => v.value)
        // Drop departments that are no longer under a picked division.
        const allowed = new Set((searchFields?.departments ?? [])
            .filter(d => divisionIds.length === 0 || (d.parentId !== null && divisionIds.includes(d.parentId)))
            .map(d => d.id))
        onChange({ ...filters, divisionIds, departmentIds: filters.departmentIds.filter(id => allowed.has(id)) })
    }

    return <SearchFormContainer
        compact
        inputs={<>
            <SearchSelect
                containerStyle={{ flex: "1 100px", minWidth: "100px" }}
                label="Fiscal Year"
                options={fiscalYears}
                value={String(filters.fiscalYear)}
                onChange={v => v && onChange({ ...filters, fiscalYear: Number(v.value) })}
                placeholder="Select year"
            />
            <MultiSelect
                containerStyle={{ flex: "3 180px" }}
                label="Department"
                options={departmentOptions}
                value={pick(departmentOptions, filters.departmentIds)}
                onChange={v => onChange({ ...filters, departmentIds: v.map(o => o.value) })}
                placeholder="All departments"
            />
            <MultiSelect
                containerStyle={{ flex: "3 180px" }}
                label="Division"
                options={divisionOptions}
                value={pick(divisionOptions, filters.divisionIds)}
                onChange={onChangeDivisions}
                placeholder="All divisions"
            />
            <MultiSelect
                containerStyle={{ flex: "2 160px" }}
                label="Close Month"
                options={monthOptions}
                value={pick(monthOptions, filters.months)}
                onChange={v => onChange({ ...filters, months: v.map(o => Number(o.value)).sort((a, b) => a - b) })}
                placeholder="All months"
            />
            <MultiSelect
                containerStyle={{ flex: "2 160px" }}
                label="Branch"
                options={branchOptions}
                value={pick(branchOptions, filters.branchIds)}
                onChange={v => onChange({ ...filters, branchIds: v.map(o => o.value) })}
                placeholder="All branches"
            />
            <TextInput
                containerStyle={{ flex: "3 200px" }}
                label="Description"
                placeholder="Search and press Enter"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") applySearch() }}
                onBlur={applySearch}
            />
        </>}
        clearSearchButton={<PrimaryButton onClick={onClear}>Clear Search</PrimaryButton>}
    />
}

export default SearchOpportunityDashboardForm
